import React, { useEffect, useState, useMemo, useContext } from "react";
import { Row, Col, Card, Tooltip, Typography, Button, Rate, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import { IconSearch, IconTruck } from "@tabler/icons-react";
import ShopCardSkeleton from "../skeletons/ShopCardSkeleton";
import SafeImage from "../SafeImage";
import GlobalButton from "../../util/GlobalButton";
import GlobalTooltip from "../GlobalTooltip";
import ShopServ from "../../service/ShopServ";
import { usePreference } from "../../context/PreferenceContext";
import { useDeliveryEstimateContext } from "../../context/DeliveryEstimateContext";
import { LocationContext } from "../../LocationContext";
import { capitalizeFirst, renderSafeText } from "../../util/unitFormatter";
const { Title, Text } = Typography;

const HomeShopList = ({ filters, latitude, longitude, radius, onReset, categories, onShopsLoaded }) => {
    const { t } = usePreference();
    const navigate = useNavigate();
    const [shops, setShops] = useState([]);
    const [shopStatuses, setShopStatuses] = useState({});
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const {
        selectedAddress, setSelectedAddress,
        cityName, setCityName,
        setLatitude, setLongitude,

        isManualLocation, setIsManualLocation
    } = useContext(LocationContext);
    const initialLimit = 12;

    const [activeTabId, setActiveTabId] = useState("all");

    const shopIds = useMemo(() => shops.map(s => s.id), [shops]);

    // Use categories from DB for tabs
    const availableCategories = useMemo(() => {
        if (!categories || categories.length === 0) return [{ id: 'all', name: "All" }];

        // Filter for main categories (those without a parent)
        // Returning objects with ID and Name so we can do better filtering later
        const mainCats = categories
            .filter(c => !c.parentId)
            .map(c => ({ id: c._id || c.id, name: c.name }));

        return [{ id: 'all', name: "All" }, ...mainCats];
    }, [categories]);

    // Use the shared app-wide delivery estimate context
    const { registerShop, formatETA } = useDeliveryEstimateContext();

    useEffect(() => {
        const handleShopStatusUpdate = (e) => {
            const data = e.detail;
            if (data?.shopId) {
                setShopStatuses(prev => ({
                    ...prev,
                    [data.shopId]: data.availability
                }));
            }
        };
        window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
        return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
    }, []);

    useEffect(() => {
        shopIds.forEach(id => registerShop(id));
    }, [shopIds]);

    // Fetch Shops when filters or location change
    useEffect(() => {
        const fetchShops = async () => {
            setLoading(true);
            try {
                const params = {};
                if (filters.search) params.name = filters.search;
                if (filters.zipcode) params.zipCode = filters.zipcode;
                if (latitude && longitude) {
                    params.lat = latitude;
                    params.lng = longitude;
                    params.radius = radius;
                }

                // Pass arrays as comma-separated strings
                if (filters.categories && filters.categories.length > 0) params.categories = filters.categories.join(',');
                if (filters.brands && filters.brands.length > 0) params.brands = filters.brands.join(',');

                const response = await ShopServ.index(params);
                const shopsData = response?.data || [];

                let formatted = shopsData?.map((shop) => ({
                    id: shop?._id || shop?.id,
                    ...shop,
                    name: shop?.name || "Unnamed Shop",
                    description: shop?.description || "No description available",
                    imageUrl: shop?.imageUrl || "https://via.placeholder.com/200?text=No+Image",
                    rating: shop?.rating || 0,
                    ratingCount: shop?.ratingCount || 0,
                }));

                // Client-side filtering logic
                if (filters.rating > 0) {
                    formatted = formatted.filter(shop => (shop.rating || 0) >= filters.rating);
                }

                // Client-side sorting logic
                if (filters.sort) {
                    const [field, order] = filters.sort.split('-');
                    formatted.sort((a, b) => {
                        if (field === 'name') {
                            return order === 'asc'
                                ? a.name.localeCompare(b.name)
                                : b.name.localeCompare(a.name);
                        }
                        if (field === 'rating') {
                            return (b.rating || 0) - (a.rating || 0); // Top rated first
                        }
                        if (field === 'newest') {
                            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                        }
                        return 0;
                    });
                }

                setShops(formatted);
                if (onShopsLoaded) {
                    onShopsLoaded(formatted.length);
                }
                // Reset active tab if it's not in the new results
                // However, user said "All tab should be selected by default"
                // and we usually don't want to reset if the user specifically picked a tab.
                // But if the category is gone, we should fallback to "All".
            } catch (err) {
                console.error("Error fetching shops:", err);
                setShops([]);
                if (onShopsLoaded) {
                    onShopsLoaded(0);
                }
            } finally {
                setLoading(false);
            }
        };

        // Debounce fetching handled by parent hook usually, but here we depend on props changes
        // Assuming parent passes *debounced* filters or we debounce here.
        // Given Shops.js passed debouncedFilters, we assume `filters` prop IS debounced.
        fetchShops();
    }, [filters, latitude, longitude, radius]);

    useEffect(() => {
        // If the current tab ID is no longer valid, reset to "all"
        const isValid = availableCategories.some(cat => cat.id === activeTabId);
        if (activeTabId !== "all" && !isValid) {
            setActiveTabId("all");
        }
    }, [availableCategories, activeTabId]);

    const textSlice = (text, limit = 65) => {
        if (!text) return "";
        return text.length > limit ? text.slice(0, limit) + "..." : text;


    };
    const hasShowMoreBtn = !loading && shops.filter(shop => {
        if (activeTabId === "all") return true;
        const activeCatObj = categories.find(c => (c._id || c.id) === activeTabId);
        if (!activeCatObj) return true;
        const activeCatName = activeCatObj.name.toLowerCase().trim();
        const shopCatStr = shop.business?.category || shop.category || "";
        const shopCatId = shop.categoryId?._id || shop.categoryId;
        let shopCatName = "";
        if (typeof shopCatStr === 'string' && shopCatStr.length > 0) {
            shopCatName = shopCatStr;
        } else if (shopCatId && categories && categories.length > 0) {
            const catObj = categories.find(c => (c._id || c.id) === shopCatId);
            if (catObj) shopCatName = catObj.name;
        }
        if (!shopCatName) return false;
        const shopCatLower = shopCatName.toLowerCase().trim();
        if (shopCatLower === activeCatName || activeCatName.includes(shopCatLower) || shopCatLower.includes(activeCatName)) return true;
        return categories.some(c => {
            const parentId = c.parentId?._id || c.parentId;
            return parentId === activeTabId && c.name.toLowerCase().trim() === shopCatLower;
        });
    }).length > initialLimit;

    return (
        <div className="shop-page-main-container">
            <Row gutter={[0, 24]}>
                <Col span={24}>
                    {(loading || shops.length > 0) && (
                        <div className="deals-header" style={{ justifyContent: hasShowMoreBtn ? 'space-between' : 'center' }}>
                            <div
                                className="deals-header-content"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                <span style={{ fontWeight: "700" }}>All store in</span>

                                <span style={{ color: "green", fontWeight: "600", fontSize: "22px" }}>
                                    {cityName}
                                </span>

                                <span style={{ fontWeight: "700" }}> Area</span>
                            </div>

                            {/* View More / View Less button — right side of header */}
                            {hasShowMoreBtn && (
                                    !expanded ? (
                                        <Button
                                            onClick={() => setExpanded(true)}
                                            className="premium-view-more-btn"
                                        >
                                            {t('view_more_shops', 'View More Shops')}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => setExpanded(false)}
                                            className="premium-view-more-btn"
                                        >
                                            {t('view_less_shops', 'View Less')}
                                        </Button>
                                    )
                                )}
                        </div>
                    )}

                    {/* Category Filter Tabs */}
                    {!loading && shops.length > 0 && (
                        <div className="shop-categories-bar-container">
                            <div className="shop-categories-bar" style={{ justifyContent: hasShowMoreBtn ? 'flex-start' : 'center' }}>
                                {availableCategories.map(cat => (
                                    <div
                                        key={cat.id}
                                        className={`shop-category-tab ${activeTabId === cat.id ? 'active' : ''}`}
                                        onClick={() => setActiveTabId(cat.id)}
                                    >
                                        {renderSafeText(cat.name)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}


                    <div className="shop-list-container">
                        {loading ? (
                            <div className="shops-mixed-grid">
                                <Row gutter={[20, 20]} className="mobile-scroll-row">
                                    {[...Array(12)].map((_, index) => (
                                        <Col xs={24} sm={12} md={10} lg={8} xl={6} xxl={4} key={index}>
                                            <div className="shop-card-horizontal" style={{ cursor: 'default', height: '120px' }}>
                                                <div className="shop-card-horizontal-img pcu-bg-muted" style={{ width: '100px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', width: '40px', height: '40px', opacity: 0.3 }}>
                                                        <div style={{ background: '#ddd', borderRadius: '4px' }}></div>
                                                        <div style={{ background: '#ddd', borderRadius: '50%' }}></div>
                                                        <div style={{ background: '#ddd', borderRadius: '50%' }}></div>
                                                        <div style={{ background: '#ddd', borderRadius: '4px' }}></div>
                                                    </div>
                                                    <Skeleton.Node active style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0 }} />
                                                </div>
                                                <div className="shop-card-horizontal-info" style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                    <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 2, width: ['100%', '60%'] }} />
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ) : shops.length === 0 ? (
                            <div
                                className="no-results-container"
                                style={{
                                    border: 'none',
                                    boxShadow: 'none',
                                    background: 'transparent',
                                    padding: '60px 20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center'
                                }}
                            >
                                <IconSearch size={48} color="var(--color-border-primary)" className="no-results-icon" style={{ marginBottom: '16px' }} />
                                <Title level={4} className="pcu-color-subtitle" style={{ marginBottom: '8px' }}>Please change the location to explore shops.</Title>
                                <Text type="secondary" className="pcu-color-muted" style={{ marginBottom: '24px' }}>We couldn't find any stores in this area.</Text>
                                <Button
                                    type="primary"
                                    size="large"
                                    style={{ borderRadius: '8px', fontWeight: 'bold', padding: '0 32px' }}
                                    onClick={() => {
                                        window.dispatchEvent(new CustomEvent('open-location-prompt'));
                                    }}
                                >
                                    {t('choose_location') || 'Change Location'}
                                </Button>
                            </div>
                        ) : (
                            <div className="shops-mixed-grid">
                                <Row gutter={[20, 20]} className="mobile-scroll-row">
                                    {shops
                                        .filter(shop => {
                                            if (activeTabId === "all") return true;

                                            // 1. Get the current active category object from our DB list
                                            const activeCatObj = categories.find(c => (c._id || c.id) === activeTabId);
                                            if (!activeCatObj) return true; // Safety fallback

                                            const activeCatName = activeCatObj.name.toLowerCase().trim();

                                            // 2. Identify the shop's category (Name or ID)
                                            const shopCatStr = shop.business?.category || shop.category || "";
                                            const shopCatId = shop.categoryId?._id || shop.categoryId;

                                            // 3. Resolve the actual category name for the shop
                                            let shopCatName = "";
                                            if (typeof shopCatStr === 'string' && shopCatStr.length > 0) {
                                                shopCatName = shopCatStr;
                                            } else if (shopCatId && categories && categories.length > 0) {
                                                const catObj = categories.find(c => (c._id || c.id) === shopCatId);
                                                if (catObj) shopCatName = catObj.name;
                                            }

                                            if (!shopCatName) return false;

                                            const shopCatLower = shopCatName.toLowerCase().trim();

                                            // 4. Exact or partial string match with active category
                                            if (shopCatLower === activeCatName || activeCatName.includes(shopCatLower) || shopCatLower.includes(activeCatName)) return true;

                                            // 5. Hierarchical match (check if shop's category is a child of the active tab)
                                            const isChildMatch = categories.some(c => {
                                                const parentId = c.parentId?._id || c.parentId;
                                                return parentId === activeTabId && c.name.toLowerCase().trim() === shopCatLower;
                                            });

                                            return isChildMatch;
                                        })
                                        .slice(0, expanded ? undefined : initialLimit)
                                        .map((shop) => {
                                            const status = shopStatuses[shop.id] || { isOpen: true };
                                            return (
                                                <Col xs={24} sm={12} md={10} lg={8} xl={6} xxl={4} key={shop.id}>
                                                    <div
                                                        className={`shop-card-horizontal ${!status.isOpen ? 'pcu-opacity-60' : ''}`}
                                                        onClick={() => status.isOpen && navigate(`/products/${shop.id}`)}
                                                        style={{ cursor: status.isOpen ? 'pointer' : 'not-allowed' }}
                                                    >
                                                        <div className="shop-card-horizontal-img">
                                                            <SafeImage
                                                                alt={renderSafeText(shop.name)}
                                                                src={shop.imageUrl}
                                                                placeholderText="Shop"
                                                            />
                                                            {!status.isOpen ? (
                                                                <div style={{ position: 'absolute', bottom: '0', left: '0', right: '0', background: 'rgba(220, 38, 38, 0.9)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                                                                    {status.reopenMessage || "Closed"}
                                                                </div>
                                                            ) : (
                                                                shop.rating > 0 ? (
                                                                    <div className="shop-rating-floating-badge">
                                                                        <span className="star-icon">★</span>
                                                                        <span className="rating-value">{shop.rating.toFixed(1)}</span>
                                                                    </div>
                                                                ) : null
                                                            )}
                                                        </div>
                                                        <div className="shop-card-horizontal-info">
                                                            <div className="shop-card-horizontal-header-row">
                                                                <Title level={5} className="shop-horizontal-title">{renderSafeText(shop.name)}</Title>
                                                            </div>

                                                            <Text type="secondary" className="shop-horizontal-desc">
                                                                {textSlice(
                                                                    renderSafeText(
                                                                        shop.description || t('fresh_products_daily', 'Fresh products daily from your local store')
                                                                    ),
                                                                    55
                                                                )}
                                                            </Text>
                                                            <div className="shop-card-horizontal-footer">
                                                                <div className="footer-meta">
                                                                    {(shop.estimatedDeliveryTime || formatETA(shop.id, shop.deliveryTime || shop.business?.deliveryTime || "30-45 mins")) && (
                                                                        <div className="delivery-eta-pill">
                                                                            <IconTruck size={14} />
                                                                            Delivery by  <span>
                                                                                {shop.estimatedDeliveryTime ||
                                                                                    formatETA(
                                                                                        shop.id,
                                                                                        shop.deliveryTime ||
                                                                                        shop.business?.deliveryTime ||
                                                                                        "30-45 mins"
                                                                                    )}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                </Row>



                            </div>
                        )}



                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default HomeShopList;
